import { Action } from '@ngrx/store';
import { AutoRedemptionUpdate, MFAOutcome } from '@rd/core/mfa/types';
import { HttpError } from '@rd/types';
import {
  AutoRedemptionSetting,
  AutoRedemptionSettingStrategy,
  PeriodAutoRedemptionSettingStrategyOptions,
} from '@rd/types/auto-redemption-setting.type';
import {
  CreateCashBackAutoRedemptionSetting,
  CreateDigitalGiftCardAutoRedemptionSetting,
  CreatePhysicalGiftCardAutoRedemptionSetting,
  CreatePointsTransferAutoRedemptionSetting,
} from '../../services/auto-redemption-setting-api/auto-redemption-setting-api.service';

export enum AutoRedemptionSettingTypes {
  LOAD_AUTO_REDEMPTION_SETTINGS = '[AutoRedemptionSetting] Load auto redemption setting',
  LOAD_AUTO_REDEMPTION_SETTINGS_SUCCESS = '[AutoRedemptionSetting] Load auto redemption setting success',
  LOAD_AUTO_REDEMPTION_SETTINGS_FAIL = '[AutoRedemptionSetting] Load auto redemption setting fail',

  DELETE_AUTO_REDEMPTION_SETTING = '[AutoRedemptionSetting] Delete auto redemption setting',
  DELETE_AUTO_REDEMPTION_SETTING_SUCCESS = '[AutoRedemptionSetting] Delete auto redemption setting success',
  DELETE_AUTO_REDEMPTION_SETTING_FAIL = '[AutoRedemptionSetting] Delete auto redemption setting fail',

  UPDATE_AUTO_REDEMPTION_SETTING = '[AutoRedemptionSetting] Update auto redemption setting',
  UPDATE_AUTO_REDEMPTION_SETTING_SUCCESS = '[AutoRedemptionSetting] Update auto redemption setting success',
  UPDATE_AUTO_REDEMPTION_SETTING_FAIL = '[AutoRedemptionSetting] Update auto redemption setting fail',

  CREATE_AUTO_REDEMPTION_SETTING = '[AutoRedemptionSetting] Create auto redemption setting',
  CREATE_AUTO_REDEMPTION_SETTING_SUCCESS = '[AutoRedemptionSetting] Create auto redemption setting success',
  CREATE_AUTO_REDEMPTION_SETTING_FAIL = '[AutoRedemptionSetting] Create auto redemption setting fail',

  SUBMIT_AUTO_REDEMPTION_SETTING_FORM = '[AutoRedemptionSetting] Submit auto redemption setting form',
  CLEAR_AUTO_REDEMPTION_SETTING_FORM = '[AutoRedemptionSetting] Clear auto redemption setting form',

  HYDRATE_AUTO_REDEMPTION_SETTING = '[AutoRedemptionSetting] Hydrate auto redemption setting',
  HYDRATE_AND_UPDATE_AUTO_REDEMPTION_SETTING = '[AutoRedemptionSetting] Hydrate and Update auto redemption setting',

  RESET_AUTO_REDEMPTION_SETTING_STATE = '[AutoRedemptionSetting] Reset auto redemption setting state',
}

export class LoadAutoRedemptionSettings implements Action {
  readonly type = AutoRedemptionSettingTypes.LOAD_AUTO_REDEMPTION_SETTINGS;
}

export class LoadAutoRedemptionSettingsSuccess implements Action {
  readonly type =
    AutoRedemptionSettingTypes.LOAD_AUTO_REDEMPTION_SETTINGS_SUCCESS;

  constructor(public payload: AutoRedemptionSetting[]) {}
}

export class LoadAutoRedemptionSettingsFail implements Action {
  readonly type = AutoRedemptionSettingTypes.LOAD_AUTO_REDEMPTION_SETTINGS_FAIL;

  constructor(public payload: HttpError) {}
}

export class DeleteAutoRedemptionSetting implements Action {
  constructor(
    public payload: {
      id: string;
      loyaltyProgramId?: string;
    },
  ) {}

  readonly type = AutoRedemptionSettingTypes.DELETE_AUTO_REDEMPTION_SETTING;
}

export class DeleteAutoRedemptionSettingSuccess implements Action {
  readonly type =
    AutoRedemptionSettingTypes.DELETE_AUTO_REDEMPTION_SETTING_SUCCESS;

  constructor(public payload?: { loyaltyProgramId?: string }) {}
}

export class DeleteAutoRedemptionSettingFail implements Action {
  readonly type =
    AutoRedemptionSettingTypes.DELETE_AUTO_REDEMPTION_SETTING_FAIL;

  constructor(public payload: HttpError) {}
}

export class UpdateAutoRedemptionSetting implements Action {
  constructor(
    public payload: {
      id: string;
      pointsRequired: number;
      strategy: AutoRedemptionSettingStrategy;
      strategyOptions: PeriodAutoRedemptionSettingStrategyOptions | {};
    },
  ) {}

  readonly type = AutoRedemptionSettingTypes.UPDATE_AUTO_REDEMPTION_SETTING;
}

export class HydrateAndUpdateAutoRedemptionSetting implements Action {
  constructor(
    public payload: {
      id: string;
      pointsRequired: number;
      strategy: AutoRedemptionSettingStrategy;
      strategyOptions: PeriodAutoRedemptionSettingStrategyOptions | {};
    },
  ) {}

  readonly type =
    AutoRedemptionSettingTypes.HYDRATE_AND_UPDATE_AUTO_REDEMPTION_SETTING;
}

export class UpdateAutoRedemptionSettingSuccess implements Action {
  readonly type =
    AutoRedemptionSettingTypes.UPDATE_AUTO_REDEMPTION_SETTING_SUCCESS;

  constructor(public payload: AutoRedemptionSetting) {}
}

export class UpdateAutoRedemptionSettingFail implements Action {
  readonly type =
    AutoRedemptionSettingTypes.UPDATE_AUTO_REDEMPTION_SETTING_FAIL;

  constructor(public payload: HttpError) {}
}

export class CreateAutoRedemptionSetting implements Action {
  constructor(
    public payload:
      | CreatePointsTransferAutoRedemptionSetting
      | CreateCashBackAutoRedemptionSetting
      | CreatePhysicalGiftCardAutoRedemptionSetting
      | CreateDigitalGiftCardAutoRedemptionSetting,
    public isEverydaySpend = false,
  ) {}

  readonly type = AutoRedemptionSettingTypes.CREATE_AUTO_REDEMPTION_SETTING;
}

export class CreateAutoRedemptionSettingSuccess implements Action {
  readonly type =
    AutoRedemptionSettingTypes.CREATE_AUTO_REDEMPTION_SETTING_SUCCESS;

  constructor(
    public payload: AutoRedemptionSetting,
    public isEverydaySpend = false,
  ) {}
}

export class CreateAutoRedemptionSettingFail implements Action {
  readonly type =
    AutoRedemptionSettingTypes.CREATE_AUTO_REDEMPTION_SETTING_FAIL;

  constructor(public payload: HttpError) {}
}

export class SubmitAutoRedemptionSettingForm implements Action {
  readonly type =
    AutoRedemptionSettingTypes.SUBMIT_AUTO_REDEMPTION_SETTING_FORM;

  constructor(
    public payload:
      | CreatePointsTransferAutoRedemptionSetting
      | CreateCashBackAutoRedemptionSetting
      | CreatePhysicalGiftCardAutoRedemptionSetting
      | CreateDigitalGiftCardAutoRedemptionSetting,
    public isEverydaySpend = false,
  ) {}
}

export class ClearRedemptionSettingForm implements Action {
  readonly type = AutoRedemptionSettingTypes.CLEAR_AUTO_REDEMPTION_SETTING_FORM;
}

export class HydrateAutoRedemptionSetting implements Action {
  readonly type = AutoRedemptionSettingTypes.HYDRATE_AUTO_REDEMPTION_SETTING;

  constructor(
    public payload: {
      autoRedemptionUpdateHydrationState: AutoRedemptionUpdate;
      outcome: MFAOutcome;
    },
  ) {}
}

export class ResetAutoRedemptionSettingState implements Action {
  readonly type =
    AutoRedemptionSettingTypes.RESET_AUTO_REDEMPTION_SETTING_STATE;
}

export type AutoRedemptionSettingsActions =
  | LoadAutoRedemptionSettings
  | LoadAutoRedemptionSettingsSuccess
  | LoadAutoRedemptionSettingsFail
  | DeleteAutoRedemptionSetting
  | DeleteAutoRedemptionSettingSuccess
  | DeleteAutoRedemptionSettingFail
  | UpdateAutoRedemptionSetting
  | UpdateAutoRedemptionSettingSuccess
  | UpdateAutoRedemptionSettingFail
  | CreateAutoRedemptionSetting
  | CreateAutoRedemptionSettingSuccess
  | CreateAutoRedemptionSettingFail
  | SubmitAutoRedemptionSettingForm
  | ClearRedemptionSettingForm
  | HydrateAutoRedemptionSetting
  | HydrateAndUpdateAutoRedemptionSetting
  | ResetAutoRedemptionSettingState;
